import React from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { AuthService } from '../../services/AuthService';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { currentUser, loading, userProfile } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    try {
      await AuthService.signOutUser();
    } catch (err) {
      console.error('Sign-out failed:', err);
    }
    navigate('/login', { replace: true });
  };

  try {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
          <CircularProgress />
        </Box>
      );
    }

    if (currentUser && !userProfile) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
            gap: 2,
            p: 3,
            textAlign: 'center',
          }}
        >
          <Typography>
            We could not open your account. Log out and sign in again.
          </Typography>
          <Button variant="contained" onClick={handleSignOut}>
            Log out
          </Button>
        </Box>
      );
    }

    if (!currentUser) {
      return <Navigate to="/login" state={{ from: location }} replace />;
    }

    if (userProfile?.mustChangePassword && location.pathname !== '/profile') {
      return <Navigate to="/profile?forcePasswordChange=1" replace />;
    }

    return <>{children}</>;
  } catch (error) {
    console.error('ProtectedRoute - error:', error);
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Typography color="error">Error loading protected route</Typography>
      </Box>
    );
  }
};
